import { useState } from 'react';

interface Reaction {
  id: string;
  emoji: string;
  label: string;
  count: number;
  users: string[];
  color: string;
}

interface ExpandedReactionsProps {
  postId: string;
  onReact?: (reactionId: string) => void;
}

export default function ExpandedReactions({ postId, onReact }: ExpandedReactionsProps) {
  const [showPicker, setShowPicker] = useState(false);
  const [reactions, setReactions] = useState<Reaction[]>([
    { id: 'congrats', emoji: '👏', label: 'Parabéns', count: 24, users: ['Maria', 'João', 'Ana'], color: 'bg-yellow-100 text-yellow-600' },
    { id: 'fire', emoji: '🔥', label: 'Incrível', count: 18, users: ['Carlos', 'Paula'], color: 'bg-orange-100 text-orange-600' },
    { id: 'strong', emoji: '💪', label: 'Motivador', count: 15, users: ['Pedro', 'Lucia'], color: 'bg-blue-100 text-blue-600' },
    { id: 'wow', emoji: '😮', label: 'Uau', count: 12, users: ['Rafael'], color: 'bg-purple-100 text-purple-600' },
    { id: 'love', emoji: '❤️', label: 'Amei', count: 31, users: ['Fernanda', 'Roberto', 'Camila'], color: 'bg-red-100 text-red-600' }
  ]);
  const [userReaction, setUserReaction] = useState<string | null>(null);
  const [showUsersModal, setShowUsersModal] = useState<string | null>(null);

  const handleReaction = (reactionId: string) => {
    setReactions(prev =>
      prev.map(r => {
        if (r.id === reactionId) {
          const isRemoving = userReaction === reactionId;
          return {
            ...r,
            count: isRemoving ? r.count - 1 : r.count + (userReaction ? 0 : 1),
            users: isRemoving
              ? r.users.filter(u => u !== 'Você')
              : [...(userReaction ? r.users : r.users), 'Você']
          };
        }
        if (r.id === userReaction) {
          return {
            ...r,
            count: r.count - 1,
            users: r.users.filter(u => u !== 'Você')
          };
        }
        return r;
      })
    );

    setUserReaction(userReaction === reactionId ? null : reactionId);
    setShowPicker(false);
    onReact?.(reactionId);
  };

  const totalReactions = reactions.reduce((sum, r) => sum + r.count, 0);

  return (
    <div className="relative">
      {/* Reactions Summary */}
      <div className="flex items-center gap-2 flex-wrap">
        {reactions.filter(r => r.count > 0).map(reaction => (
          <button
            key={reaction.id}
            onClick={() => setShowUsersModal(reaction.id)}
            className={`flex items-center gap-1 px-3 py-1.5 rounded-full transition-all hover:scale-110 ${
              reaction.color
            } ${userReaction === reaction.id ? 'ring-2 ring-offset-1 ring-teal-500' : ''}`}
          >
            <span className="text-lg">{reaction.emoji}</span>
            <span className="text-sm font-semibold">{reaction.count}</span>
          </button>
        ))}

        {/* Add Reaction Button */}
        <div className="relative">
          <button
            onClick={() => setShowPicker(!showPicker)}
            className="flex items-center gap-1 px-3 py-1.5 rounded-full bg-gray-100 hover:bg-gray-200 transition-all hover:scale-110 text-gray-700"
          >
            <i className="ri-add-line text-lg"></i>
            <span className="text-sm font-medium">Reagir</span>
          </button>

          {/* Reaction Picker */}
          {showPicker && (
            <>
              <div
                className="fixed inset-0 z-40"
                onClick={() => setShowPicker(false)}
              ></div>
              <div className="absolute bottom-full left-0 mb-2 bg-white rounded-2xl shadow-2xl p-3 z-50 animate-in fade-in slide-in-from-bottom-2 duration-200">
                <div className="flex items-center gap-2">
                  {reactions.map(reaction => (
                    <button
                      key={reaction.id}
                      onClick={() => handleReaction(reaction.id)}
                      className="group relative flex flex-col items-center gap-1 p-2 rounded-xl hover:bg-gray-100 transition-all"
                      title={reaction.label}
                    >
                      <span className="text-3xl transform group-hover:scale-125 transition-transform">
                        {reaction.emoji}
                      </span>
                      <span className="text-xs text-gray-600 opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap">
                        {reaction.label}
                      </span>
                    </button>
                  ))}
                </div>
              </div>
            </>
          )}
        </div>
      </div>

      {/* Users Modal */}
      {showUsersModal && (
        <>
          <div
            className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
            onClick={() => setShowUsersModal(null)}
          >
            <div
              className="bg-white rounded-2xl shadow-2xl max-w-md w-full max-h-[600px] overflow-hidden"
              onClick={e => e.stopPropagation()}
            >
              {/* Modal Header */}
              <div className="p-6 border-b border-gray-200">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <span className="text-4xl">
                      {reactions.find(r => r.id === showUsersModal)?.emoji}
                    </span>
                    <div>
                      <h3 className="font-bold text-lg text-gray-900">
                        {reactions.find(r => r.id === showUsersModal)?.label}
                      </h3>
                      <p className="text-sm text-gray-600">
                        {reactions.find(r => r.id === showUsersModal)?.count} pessoas
                      </p>
                    </div>
                  </div>
                  <button
                    onClick={() => setShowUsersModal(null)}
                    className="p-2 hover:bg-gray-100 rounded-full transition-colors"
                  >
                    <i className="ri-close-line text-2xl text-gray-600"></i>
                  </button>
                </div>
              </div>

              {/* Users List */}
              <div className="p-6 overflow-y-auto max-h-[400px]">
                {reactions
                  .find(r => r.id === showUsersModal)
                  ?.users.map((user, index) => (
                    <div
                      key={index}
                      className="flex items-center gap-3 p-3 rounded-xl hover:bg-gray-50 transition-colors"
                    >
                      <img
                        src={`https://readdy.ai/api/search-image?query=professional%20person%20smiling%20portrait%20business%20casual%20friendly%20natural%20lighting%20studio%20background&width=100&height=100&seq=user${index}&orientation=squarish`}
                        alt={user}
                        className="w-12 h-12 rounded-full object-cover"
                      />
                      <div className="flex-1">
                        <p className="font-semibold text-gray-900">{user}</p>
                        <p className="text-sm text-gray-600">Nível {10 + index}</p>
                      </div>
                      {user === 'Você' && (
                        <span className="text-xs bg-teal-100 text-teal-700 px-2 py-1 rounded-full font-medium">
                          Você
                        </span>
                      )}
                    </div>
                  ))}
              </div>
            </div>
          </div>
        </>
      )}

      {/* Total Count */}
      {totalReactions > 0 && (
        <p className="text-sm text-gray-600 mt-2">
          {totalReactions} {totalReactions === 1 ? 'reação' : 'reações'}
        </p>
      )}
    </div>
  );
}
